import { apiClient } from './client';
import type { StatusConfig, ModuleVisibility, ColumnConfig, DocumentConfig } from './config';
import type { CitySectionVisibility, InseratSectionVisibility, MapCenter } from '../types';

export interface CityListItem {
  id: number;
  name: string;
  timezone: string;
  is_active: boolean;
  logo_url: string | null;
  user_count: number;
  listing_count: number;
  created_at: string;
}

export interface CityResponse {
  id: number;
  name: string;
  timezone: string;
  is_active: boolean;
  status_config: unknown | null;
  logo_url: string | null;
  default_plz: string | null;
  default_map_center: MapCenter | null;
  created_at: string;
  updated_at: string | null;
}

export interface CityUpdateData {
  name?: string;
  timezone?: string;
  is_active?: boolean;
  default_plz?: string | null;
}

export interface CityCreateData {
  name: string;
  timezone?: string;
}

export const citiesApi = {
  list: async (): Promise<{ items: CityListItem[]; total: number }> => {
    const { data } = await apiClient.get('/cities/');
    return data;
  },

  get: async (id: number): Promise<CityResponse> => {
    const { data } = await apiClient.get(`/cities/${id}`);
    return data;
  },

  create: async (payload: CityCreateData): Promise<CityResponse> => {
    const { data } = await apiClient.post('/cities/', payload);
    return data;
  },

  update: async (id: number, data: CityUpdateData): Promise<CityResponse> => {
    const res = await apiClient.put(`/cities/${id}`, data);
    return res.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/cities/${id}`);
  },

  uploadLogo: async (id: number, file: File): Promise<CityResponse> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post(`/cities/${id}/logo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  deleteLogo: async (id: number): Promise<CityResponse> => {
    const { data } = await apiClient.delete(`/cities/${id}/logo`);
    return data;
  },

  getSectionVisibility: async (id: number): Promise<CitySectionVisibility> => {
    const { data } = await apiClient.get(`/cities/${id}/section-visibility`);
    return data;
  },

  setSectionVisibility: async (
    id: number,
    payload: { inserat?: Partial<InseratSectionVisibility> },
  ): Promise<CitySectionVisibility> => {
    const { data } = await apiClient.put(`/cities/${id}/section-visibility`, payload);
    return data;
  },

  getStatusConfig: async (id: number): Promise<StatusConfig> => {
    const { data } = await apiClient.get(`/cities/${id}/status-config`);
    return data;
  },

  setStatusConfig: async (id: number, payload: Partial<StatusConfig>): Promise<StatusConfig> => {
    const { data } = await apiClient.put(`/cities/${id}/status-config`, payload);
    return data;
  },

  getModuleVisibility: async (id: number): Promise<ModuleVisibility> => {
    const { data } = await apiClient.get<{ modules: ModuleVisibility }>(`/cities/${id}/module-visibility`);
    return data.modules;
  },

  setModuleVisibility: async (id: number, modules: ModuleVisibility): Promise<ModuleVisibility> => {
    const { data } = await apiClient.put<{ modules: ModuleVisibility }>(
      `/cities/${id}/module-visibility`,
      { modules },
    );
    return data.modules;
  },

  getColumnConfig: async (id: number): Promise<ColumnConfig> => {
    const { data } = await apiClient.get<{ lists: ColumnConfig }>(`/cities/${id}/column-config`);
    return data.lists;
  },

  setColumnConfig: async (id: number, lists: ColumnConfig): Promise<ColumnConfig> => {
    const { data } = await apiClient.put<{ lists: ColumnConfig }>(
      `/cities/${id}/column-config`,
      { lists },
    );
    return data.lists;
  },

  getDocumentConfig: async (id: number): Promise<DocumentConfig> => {
    const { data } = await apiClient.get<{ entities: DocumentConfig }>(`/cities/${id}/document-config`);
    return data.entities;
  },

  setDocumentConfig: async (id: number, entities: DocumentConfig): Promise<DocumentConfig> => {
    const { data } = await apiClient.put<{ entities: DocumentConfig }>(
      `/cities/${id}/document-config`,
      { entities },
    );
    return data.entities;
  },

  exportConfig: async (id: number): Promise<CityConfigBundle> => {
    const { data } = await apiClient.get<CityConfigBundle>(`/cities/${id}/config-export`);
    return data;
  },

  importConfig: async (id: number, bundle: CityConfigBundle): Promise<{ applied: string[] }> => {
    const { data } = await apiClient.post<{ applied: string[] }>(`/cities/${id}/config-import`, bundle);
    return data;
  },
};

/** Portable, tenant-independent city configuration (see backend CityConfigBundle). */
export interface CityConfigBundle {
  version: number;
  exported_from?: string | null;
  status_config?: unknown | null;
  module_visibility?: ModuleVisibility | null;
  column_config?: ColumnConfig | null;
  section_visibility?: Record<string, Record<string, boolean>> | null;
  document_config?: DocumentConfig | null;
}

